import stringify from "fast-json-stable-stringify";
import { action, observable, onBecomeObserved, onBecomeUnobserved } from "mobx";

import { BaseClient } from "@formant/core/common/BaseClient";
import { isErrorType } from "@formant/core/common/isErrorType";
import { duration } from "@formant/core/model/duration";

import { FeatureUnavailablePageError } from "../../component/FeatureUnavailablePageError";
import { GenerationFailureError } from "./GenerationFailureError";
import { IPeekOptions, IRefreshOptions, IStoreCache } from "./IStoreCache";

function isExpectedError(error: unknown): boolean {
    return (
        BaseClient.isNetworkError(error) ||
        isErrorType(error, FeatureUnavailablePageError)
    );
}

interface ICacheEntryMetadata<Value> {
    generating: Promise<Value> | false;
    staleAt: number;
}

type CacheKey = string;

export interface IStoreCacheOptions<Key = any, Value = any> {
    capacity?: number;
    staleIntervalMs?: number;
    isEqual?: (a: Value | undefined, b: Value | undefined) => boolean;
    keyToCacheKey?: (key: Key) => CacheKey;
}

export class StoreCache<Key, Value> implements IStoreCache<Key, Value> {
    @observable.shallow
    private entries = new Map<CacheKey, Value>();

    private metadata = new Map<CacheKey, ICacheEntryMetadata<Value>>();
    private readonly capacity!: number;
    private readonly staleIntervalMs!: number;
    private readonly isEqual: (
        a: Value | undefined,
        b: Value | undefined,
    ) => boolean;

    private readonly keyToCacheKey: (key: Key) => CacheKey;

    constructor(options: IStoreCacheOptions<Key, Value> = {}) {
        const {
            capacity = 10_000,
            staleIntervalMs = duration.minute,
            isEqual = () => false,
            keyToCacheKey = stringify,
        } = options;

        this.capacity = capacity;
        this.staleIntervalMs = staleIntervalMs;
        this.isEqual = isEqual;
        this.keyToCacheKey = keyToCacheKey;
    }

    peek(key: Key, options: IPeekOptions = {}): Value | undefined {
        const { useStale = true } = options;
        const cacheKey = this.keyToCacheKey(key);
        const value = this.entries.get(cacheKey);

        if (!useStale && this.isStale(cacheKey)) {
            return undefined;
        }

        return value;
    }

    get(
        key: Key,
        generator: (() => Promise<Value>) | false,
    ): Value | undefined {
        const cacheKey = this.keyToCacheKey(key);

        if (
            this.isStale(cacheKey) &&
            !this.isGenerating(cacheKey) &&
            generator
        ) {
            void this.generate(key, generator);
        }

        return this.entries.get(cacheKey);
    }

    public async getAsync(
        key: Key,
        generator: (() => Promise<Value>) | false,
    ): Promise<Value> {
        const cacheKey = this.keyToCacheKey(key);

        const isGenerating = this.isGenerating(cacheKey);
        if (isGenerating) {
            return isGenerating;
        }

        if (this.isStale(cacheKey) && generator) {
            return this.generate(key, generator);
        }

        const value = this.entries.get(cacheKey);
        if (value === undefined) {
            throw new GenerationFailureError("No generator given");
        }
        return value;
    }

    set(key: Key, value: Value) {
        const cacheKey = this.keyToCacheKey(key);
        this.metadata.set(cacheKey, {
            generating: false,
            staleAt: performance.now() + this.staleIntervalMs,
        });

        const previousValue = this.entries.get(cacheKey);
        const isDuplicate = this.isEqual(previousValue, value);
        if (!isDuplicate) {
            this.entries.set(cacheKey, value);
            this.enforceMaxSize();
        }
    }

    async refresh(
        key: Key,
        generator: () => Promise<Value>,
        options: IRefreshOptions = {},
    ): Promise<void> {
        const cacheKey = this.keyToCacheKey(key);
        const metadata = this.metadata.get(cacheKey);

        const { lazy = false } = options;
        if (lazy && !this.isStale(cacheKey)) {
            return;
        }

        if (metadata?.generating) {
            await metadata.generating;
        } else {
            await this.generate(key, generator);
        }
    }

    expire(key: Key) {
        const cacheKey = this.keyToCacheKey(key);
        const metadata = this.metadata.get(cacheKey);
        if (metadata && !metadata.generating) {
            metadata.staleAt = performance.now() - 1;
        }
    }

    clear() {
        this.entries.clear();
        [...this.metadata.values()].forEach(value => {
            value.generating = false;
            value.staleAt = performance.now() - 1;
        });
    }

    delete(key: Key): void {
        const cacheKey = this.keyToCacheKey(key);
        this.clearKey(cacheKey);
    }

    watch(options: { onStart?: () => void; onEnd?: () => void }): () => void {
        const { onStart, onEnd } = options;
        const destroyStart = onStart
            ? onBecomeObserved(this, "entries", onStart)
            : undefined;

        const destroyEnd = onEnd
            ? onBecomeUnobserved(this, "entries", onEnd)
            : undefined;

        return () => {
            destroyStart?.();
            destroyEnd?.();
        };
    }

    async awaitGeneration(...keys: Key[]): Promise<void> {
        const cacheKeys = keys.map(it => this.keyToCacheKey(it));
        const takeAll = cacheKeys.length === 0;
        const generators = takeAll
            ? [...this.metadata.values()].flatMap(
                  metadata => metadata.generating ?? [],
              )
            : [...this.metadata.entries()].flatMap(([key, metadata]) =>
                  cacheKeys.includes(key) ? (metadata.generating ?? []) : [],
              );
        await Promise.all(generators);
    }

    private clearKey(cacheKey: CacheKey): void {
        this.metadata.delete(cacheKey);
        this.entries.delete(cacheKey);
    }

    private isGenerating(cacheKey: CacheKey): Promise<Value> | false {
        const metadata = this.metadata.get(cacheKey);
        return metadata ? metadata.generating : false;
    }

    private isStale(cacheKey: CacheKey): boolean {
        const metadata = this.metadata.get(cacheKey);
        return metadata ? metadata?.staleAt < performance.now() : true;
    }

    @action
    private enforceMaxSize(): void {
        while (this.metadata.size > this.capacity && this.metadata.size > 0) {
            const [key] = [...this.metadata.entries()].reduce(
                ([oldestKey, oldestEntry], [thisKey, entry]) =>
                    entry.staleAt < oldestEntry.staleAt
                        ? [thisKey, entry]
                        : [oldestKey, oldestEntry],
            );
            this.clearKey(key);
        }
    }

    private generate(
        key: Key,
        generator: () => Promise<Value>,
    ): Promise<Value> {
        const cacheKey = this.keyToCacheKey(key);
        const existingMetadata = this.metadata.get(cacheKey) || {};

        const generating = generator()
            .then(value => {
                const metadata = this.metadata.get(cacheKey);
                const canceled = metadata?.generating !== generating;
                if (!canceled) {
                    this.set(key, value);
                }
                return value;
            })
            .catch(err => {
                this.metadata.delete(cacheKey);
                if (!isExpectedError(err)) {
                    throw new GenerationFailureError(err);
                }
                throw err;
            });

        this.metadata.set(cacheKey, {
            ...existingMetadata,
            generating,
            staleAt: performance.now() + this.staleIntervalMs,
        });

        return generating;
    }
}
